import { useState, memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import Badge from '@material-ui/core/Badge';
import NotificationBell from '@icon/NewNotificationBell';
import Dynamic from '@base/Dynamic/Dynamic';
import { mediaQuery, Spacings } from '@styles/index';
import UAIconsNotificationBell from '@icon/UAIconsNotificationBell';
import useHeaderNotifications from '@api/hooks/auth/useHeaderNotifications';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useCompeteConfig from '@cont/Compete/useCompeteConfig';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';

const getColor = (isOpen, showCompeteBackground, primeTheme) => {
  if (primeTheme) return 'var(--color-i-white)';
  if (showCompeteBackground) {
    if (isOpen) {
      return 'var(--color-text-primary)';
    }
    return 'var(--color-i-white)';
  }
  return 'var(--color-text-primary)';
};

const RenderNotifications = Dynamic(() => import('./RenderNotifications'));

const NotificationBellContainer = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${Spacings.SPACING_2B};
  align-self: center;
  cursor: pointer;
  border-radius: 50%;
  background-color: ${({ $isActive, $primeTheme }) => {
    if ($primeTheme) return 'var(--color-i-prime-card)';
    return $isActive ? 'var(--color-divider)' : 'transparent';
  }};
  transition: background-color 300ms ease-in-out;
  &:hover {
    background-color: ${({ $primeTheme }) =>
      $primeTheme ? 'var(--color-i-prime-card)' : 'var(--color-divider)'};
    ${(props) =>
      props.$showCompeteBackground
        ? css`
            path {
              stroke: var(--color-base-fill);
            }
          `
        : null}
  }
`;

const StyledBadge = styled(Badge)`
  &.MuiBadge-root .MuiBadge-badge {
    width: 8px;
    height: 8px;
    display: table;
    background-color: var(--color-i-red);
    position: absolute;
    right: 7px;
    top: 5px;
    font-weight: bold;
  }
  ${mediaQuery.uptoTablet} {
    grid-column: 17/18;
  }
`;

const HeaderNotification = ({ isUAIconsGoal, primeTheme }) => {
  const {
    query: { goalUID }
  } = useRouter();
  const selectedGoalUID = useSelectedGoalUID();
  const { showCompeteBackground } = useCompeteConfig();

  const { notifications: results } = useHeaderNotifications(
    goalUID || selectedGoalUID
  );
  const hasUnseenNotifications = useMemo(() => {
    if (results.length)
      return (
        results
          .slice(0, 10)
          .filter((item) => !item.rat && item.section === 'new').length > 0
      );
    return false;
  }, [results]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleBellOnClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  return (
    <>
      <NotificationBellContainer
        $isActive={!!anchorEl}
        $showCompeteBackground={showCompeteBackground}
        $primeTheme={primeTheme}
      >
        <StyledBadge
          invisible={!hasUnseenNotifications}
          variant="dot"
          onClick={handleBellOnClick}
        >
          {isUAIconsGoal ? (
            <UAIconsNotificationBell size="24px" />
          ) : (
            <NotificationBell
              size="24px"
              color={getColor(!!anchorEl, showCompeteBackground, primeTheme)}
            />
          )}
        </StyledBadge>
      </NotificationBellContainer>

      {anchorEl && (
        <RenderNotifications
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          results={results}
        />
      )}
    </>
  );
};

HeaderNotification.propTypes = {
  isUAIconsGoal: PropTypes.bool,
  primeTheme: PropTypes.bool
};

HeaderNotification.defaultProps = {
  isUAIconsGoal: false,
  primeTheme: false
};

export default memo(HeaderNotification);
